import type { CSSProperties, ReactNode } from 'react'
import { Box, Text } from '@mantine/core'
import { useAppTranslation } from 'hooks/useTranslation'
import type { DeskCheckName, DeskFieldId } from './checkFieldMap'

export type DeskFlagVariant = 'check' | 'changed'

/**
 * The highlight (UI-SPEC §Color, §Spacing): orange means "a check failed, look
 * here" (DESK-02), yellow means "changed by the proposal" (33-16). 4px left
 * border, 8px padding, radius 4. Never Mantine's red `error` styling: a failed
 * check does not block Approve.
 */
export const deskFlagStyle = (variant: DeskFlagVariant): CSSProperties => {
  const color = variant === 'check' ? 'orange' : 'yellow'
  return {
    borderLeft: `4px solid var(--mantine-color-${color}-6)`,
    background: `var(--mantine-color-${color}-0)`,
    padding: 8,
    borderRadius: 4
  }
}

const CAPTION_COLOR: Record<DeskFlagVariant, string> = {
  check: 'orange.8',
  changed: 'yellow.9'
}

export type DeskFieldFlagProps = {
  field: DeskFieldId
  /** The checks that flag this field, in display order (checkFieldMap.flagsByField). */
  checks: readonly DeskCheckName[]
  variant: DeskFlagVariant
  /**
   * Desk-side notes that flag the field like a check (translation keys), for
   * what the desk itself worked out rather than an editor check, e.g. a
   * placeholder time ('desk.field_note.time_not_stated'). Shown after the checks.
   */
  notes?: readonly string[]
  children: ReactNode
}

/**
 * The wrapper EventForm's `desk.renderField` returns around each form section.
 *
 * It always carries the id `desk-field-{field}`, so "What the editor flagged"
 * can scroll to the section. A flagged section gets the highlight and a caption
 * that names the check(s) in plain words, joined with " · " (color is never the
 * only signal, UI-SPEC §H). The highlight is the editor's verdict, not live
 * validation, so it stays while she edits.
 */
export const DeskFieldFlag = ({ field, checks, variant, notes = [], children }: DeskFieldFlagProps) => {
  const { t } = useAppTranslation()
  const flagged = variant === 'changed' || checks.length > 0 || notes.length > 0
  const caption = variant === 'changed'
    ? t('desk.update.changed_field')
    : [...checks.map((check) => t(`desk.checks.${check}`)), ...notes.map((note) => t(note))].join(' · ')

  if (!flagged) {
    return <Box id={`desk-field-${field}`}>{children}</Box>
  }

  return (
    <Box id={`desk-field-${field}`} data-flag={variant} style={deskFlagStyle(variant)}>
      <Text size='xs' lh={1.4} mb={4} c={CAPTION_COLOR[variant]} data-caption={variant}>
        {caption}
      </Text>
      {children}
    </Box>
  )
}
